import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Collaborator } from '@/schemas/collaborators';

interface CollaboratorsState {
  byId: Record<string, Collaborator>;
  allIds: string[];
  filters: {
    search?: string;
    isActive?: boolean;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: CollaboratorsState = {
  byId: {},
  allIds: [],
  filters: {},
  isLoading: false,
  error: null,
};

const collaboratorsSlice = createSlice({
  name: 'collaborators',
  initialState,
  reducers: {
    setCollaborators: (state, action: PayloadAction<Collaborator[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((collaborator) => {
        state.byId[collaborator.id] = collaborator;
        state.allIds.push(collaborator.id);
      });
    },
    addCollaborator: (state, action: PayloadAction<Collaborator>) => {
      const collaborator = action.payload;
      state.byId[collaborator.id] = collaborator;
      if (!state.allIds.includes(collaborator.id)) {
        state.allIds.push(collaborator.id);
      }
    },
    updateCollaborator: (state, action: PayloadAction<Collaborator>) => {
      const collaborator = action.payload;
      state.byId[collaborator.id] = collaborator;
    },
    deleteCollaborator: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setFilters: (state, action: PayloadAction<Partial<CollaboratorsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setCollaborators,
  addCollaborator,
  updateCollaborator,
  deleteCollaborator,
  setFilters,
  setLoading,
  setError,
} = collaboratorsSlice.actions;

export default collaboratorsSlice.reducer;
